import React from 'react';
import styled, { css } from 'styled-components/macro';
import { ChatMessage } from './MessageList';
import { useTranslation } from 'react-i18next';
import { calculateDiff } from 'lib/utils/time';
import { Button } from 'react-covideo-common';
import { MdDownload } from 'react-icons/md';
import { apiExpressUrl } from 'configs/api/expressApi';
import { prepareImageNameForUpload } from 'lib/utils/files';

const MessageWithDownload = styled.div<{ type: string }>`
  display: flex;
  align-items: center;
  gap: 8px;

  ${({ type }) =>
    type === 'inbound' &&
    css`
      flex-direction: row-reverse;
    `}
`;

const MessagesContainer = styled.div<{ type: string }>`
  padding: 0px 10px 20px 10px;
  display: flex;
  align-items: ${({ type }) =>
    type === 'inbound' ? 'flex-start' : 'flex-end'};
  flex-direction: column;
`;

const ReceivedMessages = styled.div<{ type: string }>`
  padding: 12px 16px;
  max-width: 352px;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  word-break: break-word;
  white-space: pre-line;
  margin-bottom: 8px;
  ${({ type }) =>
    type !== 'inbound' &&
    css`
      background: rgba(0, 27, 83, 1);
      color: rgba(255, 255, 255, 1);
      border-radius: 8px 8px 0px 8px;
    `}
  ${({ type }) =>
    type === 'inbound' &&
    css`
      background: white;
      color: #001b53;
      border-radius: 8px 8px 8px 0px;
    `}

    > img {
    max-width: 100%;
  }
`;

const ReceivedMessageInfo = styled.div<{ type: string }>`
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 27, 83, 0.6);
  text-align: ${({ type }) => (type !== 'inbound' ? 'right' : 'left')};
  width: 260px;
`;
interface SingleMessageProps {
  chat: ChatMessage;
  selectedName: string;
}

const detectUrlAndText = (txt: any) => {
  const URL_REGEX =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
  return txt?.split(' ')?.map((part: any, i: any) =>
    URL_REGEX.test(part) ? (
      <a
        key={`links-${i}`}
        target='_blank'
        href={part}
        rel='noreferrer'
        style={{ color: 'inherit' }}
      >
        {part}{' '}
      </a>
    ) : (
      part + ' '
    )
  );
};

export const SingleMessage: React.FC<SingleMessageProps> = ({
  chat,
  selectedName,
}) => {
  const { t } = useTranslation();
  const firstName = chat?.user?.firstName;

  const downloadImage = async (link: string, fileName: string) => {
    const a = document.createElement('a');
    a.href = link;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <MessagesContainer type={chat.type}>
      <MessageWithDownload type={chat.type}>
        {chat.images?.length > 0 && (
          <Button
            variant='secondary'
            icon={<MdDownload size={22} />}
            style={{ cursor: 'pointer', marginTop: 8 }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              chat.images.forEach((img: any) => {
                const link = `${apiExpressUrl}images/public/${prepareImageNameForUpload(
                  img.fileName
                )}?download=true`;
                downloadImage(link, img.fileName || 'image.jpg');
              });
            }}
          />
        )}
        <ReceivedMessages type={chat.type}>
          {chat.images.map((e: any) => {
            const link = `${apiExpressUrl}images/public/${prepareImageNameForUpload(
              e.fileName
            )}`;
            return (
              <img
                style={{ width: '400px', height: '300px' }}
                key={e.fileName}
                src={link}
                loading='lazy'
                decoding='async'
                alt={t('Chat part')}
                onClick={() => window.open(link, '_blank')}
              />
            );
          })}
          {detectUrlAndText(chat.text)}
        </ReceivedMessages>
      </MessageWithDownload>

      <ReceivedMessageInfo type={chat.type}>
        {chat.type === 'inbound'
          ? `${selectedName} ●`
          : firstName && `${firstName} ●`}{' '}
        {calculateDiff(chat.deliveredAt || chat.createdAt, t)} ●{' '}
        {chat.smsId ? 'SMS' : 'MMS'}
      </ReceivedMessageInfo>
    </MessagesContainer>
  );
};
